import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Send, X, FileText } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useChatStore, chatContextKey, ChatMessage } from '../../store/chatStore';
import type { ChatAttachment, ChatContextType } from '../../lib/supabase';
import Avatar from './Avatar';

export interface ChatPanelTheme {
  text: string;
  muted: string;
  border: string;
  inputBg: string;
  cardBg: string;
}

interface Props {
  contextType: ChatContextType;
  contextId: string;
  isDark: boolean;
  theme: ChatPanelTheme;
  emptyLabel?: string;
  className?: string;
}

const EMPTY_MESSAGES: ChatMessage[] = [];

const fmtDateTime = (d: Date) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const fmtFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageAttachment = (att: ChatAttachment) => att.mimeType.startsWith('image/');

const AttachmentChip: React.FC<{ attachment: ChatAttachment; isDark: boolean; theme: ChatPanelTheme }> = ({ attachment, isDark, theme }) => {
  if (isImageAttachment(attachment)) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" title={attachment.name}>
        <img
          src={attachment.url}
          alt={attachment.name}
          className="rounded border object-cover"
          style={{ maxHeight: 96, maxWidth: 140, borderColor: theme.border }}
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      title={attachment.name}
      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border transition-colors max-w-[220px]"
      style={{ borderColor: theme.border, color: theme.text, backgroundColor: isDark ? '#1c2129' : '#f9fafb' }}
    >
      <FileText size={12} className="flex-shrink-0" style={{ color: theme.muted }} />
      <span className="truncate">{attachment.name}</span>
      <span className="flex-shrink-0" style={{ color: theme.muted }}>{fmtFileSize(attachment.size)}</span>
    </a>
  );
};

const ChatMessageRow: React.FC<{ message: ChatMessage; isDark: boolean; theme: ChatPanelTheme }> = ({ message, isDark, theme }) => {
  const authorKey = message.authorId ?? message.authorName;
  return (
    <div className="flex items-start gap-2">
      <Avatar userId={authorKey} name={message.authorName} isDark={isDark} size="md" className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium" style={{ color: theme.text }}>{message.authorName}</span>
          <span className="text-xs" style={{ color: theme.muted }}>{fmtDateTime(message.createdAt)}</span>
        </div>
        {message.text && (
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: theme.muted }}>{message.text}</p>
        )}
        {message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {message.attachments.map((att, idx) => (
              <AttachmentChip key={idx} attachment={att} isDark={isDark} theme={theme} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Shared chat feature — message list with avatars/attachments + composer with file picker.
 * Used by both the Jobs drawer and the Stock Requests detail modal, so both features
 * look and behave identically.
 */
const ChatPanel: React.FC<Props> = ({ contextType, contextId, isDark, theme, emptyLabel = 'No messages yet.', className }) => {
  const { user } = useAuthStore();
  const key = chatContextKey(contextType, contextId);
  const messages = useChatStore(s => s.messagesByContext[key] ?? EMPTY_MESSAGES);
  const isLoading = useChatStore(s => s.loadingByContext[key] ?? false);
  const isSending = useChatStore(s => s.sendingByContext[key] ?? false);
  const error = useChatStore(s => s.errorByContext[key] ?? null);
  const sendMessage = useChatStore(s => s.sendMessage);
  const clearError = useChatStore(s => s.clearError);

  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(0);

  useEffect(() => {
    void useChatStore.getState().loadMessages(contextType, contextId);
  }, [contextType, contextId]);

  useEffect(() => {
    if (messages.length !== prevCount.current) {
      prevCount.current = messages.length;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() && pendingFiles.length === 0) return;
    const ok = await sendMessage(contextType, contextId, text, pendingFiles);
    if (ok) {
      setText('');
      setPendingFiles([]);
    }
  };

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPendingFiles(prev => [...prev, ...Array.from(files)]);
  };

  return (
    <div className={`flex flex-col h-full min-h-0 ${className ?? ''}`}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1">
        {isLoading && messages.length === 0 ? (
          <p className="text-sm py-2" style={{ color: theme.muted }}>Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm py-2" style={{ color: theme.muted }}>{emptyLabel}</p>
        ) : (
          <div className="flex flex-col gap-3 pb-1">
            {messages.map(m => (
              <ChatMessageRow key={m.id} message={m} isDark={isDark} theme={theme} />
            ))}
          </div>
        )}
      </div>

      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 flex-shrink-0">
          {pendingFiles.map((f, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
              style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.text }}
            >
              <FileText size={12} />
              <span className="truncate max-w-[140px]">{f.name}</span>
              <button
                type="button"
                onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                style={{ color: theme.muted }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-400 pt-1 flex-shrink-0">{error}</p>}

      <div className="flex gap-2 pt-3 flex-shrink-0">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="px-2 rounded transition-colors flex-shrink-0"
          style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', color: theme.text }}
          title="Attach file"
        >
          <Paperclip size={14} />
        </button>
        <textarea
          value={text}
          onChange={e => {
            setText(e.target.value);
            if (error) clearError(contextType, contextId);
          }}
          placeholder="Write a message…"
          rows={2}
          className="flex-1 text-sm px-2.5 py-1.5 rounded border resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
        />
        <button
          onClick={handleSend}
          disabled={isSending || (!text.trim() && pendingFiles.length === 0) || !user}
          className="px-2.5 rounded transition-colors disabled:opacity-40 flex-shrink-0"
          style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', color: theme.text }}
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
